import React, { useState } from 'react';
import { Plus, Search, Filter, Edit2, Trash2 } from 'lucide-react';
import Pagination from './Pagination';

const Products = ({ products = [], loading = false, onDelete, onAdd, onEdit }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [stockFilter, setStockFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const categories = [
    'All',
    ...new Set(
      products
        .map((p) => p && p.category)
        .filter((cat) => typeof cat === 'string' && cat.trim() !== '')
    ),
  ];

  const filteredProducts = products.filter((p) => {
    if (!p) return false;

    const name = (p.name || '').toString().toLowerCase();
    const category = (p.category || '').toString().toLowerCase();
    const search = searchTerm.toLowerCase();

    const matchesSearch =
      name.includes(search) ||
      category.includes(search);

    const matchesCategory =
      categoryFilter === 'All' || p.category === categoryFilter;
    const matchesStock = stockFilter === 'All' ||
                        (stockFilter === 'Low Stock' && p.stock < 40 && p.stock > 0) ||
                        (stockFilter === 'In Stock' && p.stock >= 40) ||
                        (stockFilter === 'Out of Stock' && p.stock === 0);
    
    return matchesSearch && matchesCategory && matchesStock;
  });

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterChange = () => {
    setCurrentPage(1);
  };

  return (
    <div className="ui-page animate-fade-in">
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="ui-page-title text-gradient">
              Products Catalog
            </h1>
            <p className="ui-page-subtitle">
              Manage, add, and monitor your organic shop inventory catalog.
            </p>
          </div>
          
          <button 
            onClick={onAdd}
            className="ui-btn-primary"
          >
            <Plus size={18} />
            <span>Add Product</span>
          </button>
        </div>
      </div>

      {/* Filter and Search Panel */}
      <div className="ui-filter-bar mb-6">
        <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center justify-between">
          <div className="flex items-center gap-3 bg-slate-50 border border-slate-200/80 rounded-xl px-4 py-2 flex-1">
            <Search className="text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search products by name or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 outline-none text-slate-700 bg-transparent font-medium text-sm"
            />
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider hidden sm:inline">Category:</span>
              <select
                value={categoryFilter}
                onChange={(e) => {setCategoryFilter(e.target.value); handleFilterChange();}}
                className="ui-input py-2 font-semibold border-slate-200"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider hidden sm:inline">Stock:</span>
              <select
                value={stockFilter}
                onChange={(e) => {setStockFilter(e.target.value); handleFilterChange();}}
                className="ui-input py-2 font-semibold border-slate-200"
              >
                <option value="All">All Stock</option>
                <option value="Low Stock">Low Stock</option>
                <option value="In Stock">In Stock</option>
                <option value="Out of Stock">Out of Stock</option>
              </select>
            </div>
            
            <button
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('All');
                setStockFilter('All');
                handleFilterChange();
              }}
              className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200/80 text-slate-700 rounded-xl flex items-center gap-2 font-bold text-xs uppercase tracking-wider transition-all duration-200 cursor-pointer"
            >
              <Filter size={14} /> <span>Clear</span>
            </button>
          </div>
        </div>
        
        <div className="mt-4 text-xs text-slate-400 font-bold uppercase tracking-wider">
          {loading
            ? 'Loading products...'
            : `Showing ${filteredProducts.length} of ${products.length} products`}
        </div>
      </div>

      {/* Products Table */}
      <div className="ui-table-wrap">
        <table className="ui-table">
          <thead>
            <tr>
              <th className="ui-th text-center w-24">Image</th>
              <th className="ui-th w-64">Product Name</th>
              <th className="ui-th w-40">Category</th>
              <th className="ui-th w-36 text-right">Selling Price</th>
              <th className="ui-th w-36 text-right">Costing Price</th>
              <th className="ui-th w-40 text-center">Stock</th>
              <th className="ui-th w-48 text-center">Batch / Expiry</th>
              <th className="ui-th w-32 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {paginatedProducts.map(product => {
              const isOutOfStock = product.stock === 0;
              const isLowStock = product.stock < 40 && product.stock > 0;
              return (
                <tr key={product.id} className="hover:bg-slate-50/40 transition-colors duration-150 group">
                  <td className="ui-td text-center">
                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto shadow-sm overflow-hidden border border-slate-200/50">
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <span className={`text-xl ${product.imageUrl ? 'hidden' : ''}`}>{product.image || '📦'}</span>
                    </div>
                  </td>
                  <td className="ui-td">
                    <div className="font-extrabold text-slate-800 text-sm group-hover:text-emerald-600 transition-colors">{product.name}</div>
                  </td>
                  <td className="ui-td">
                    <span className="text-slate-500 font-semibold text-xs uppercase bg-slate-100 border border-slate-200/40 px-2.5 py-1 rounded-md">{product.category}</span>
                  </td>
                  <td className="ui-td text-right font-extrabold text-slate-800">
                    ₹{product.price}
                  </td>
                  <td className="ui-td text-right font-bold text-blue-600">
                    ₹{product.castingPrice}
                  </td>
                  <td className="ui-td text-center">
                    <div className="flex flex-col items-center">
                      <span className={
                        isOutOfStock ? 'badge-danger' :
                        isLowStock ? 'badge-warning' : 'badge-success'
                      }>
                        {isOutOfStock ? 'Out of Stock' : `${product.stock} ${product.stockUnit || ''}`}
                      </span>
                    </div>
                  </td>
                  <td className="ui-td text-center">
                    <div>
                      <span className="font-mono text-xs text-slate-600 bg-slate-150/70 border border-slate-200/40 px-2 py-0.5 rounded-lg inline-block">
                        {product.batchNumber || '—'}
                      </span>
                      {product.expiryDate && (
                        <div className="text-[11px] text-slate-400 mt-1 font-semibold">
                          Exp: {product.expiryDate}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="ui-td text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <button 
                        onClick={() => onEdit && onEdit(product)}
                        className="p-2 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all border border-transparent hover:border-emerald-100/40 cursor-pointer"
                        title="Edit Product"
                      >
                        <Edit2 size={15} />
                      </button>
                      <button 
                        onClick={() => onDelete && onDelete(product.id)}
                        className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all border border-transparent hover:border-rose-100/40 cursor-pointer"
                        title="Delete Product"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {paginatedProducts.length === 0 && (
              <tr>
                <td colSpan={8} className="ui-td text-center py-12 text-slate-400 font-semibold">
                  No products found. Add a new product to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredProducts.length}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default Products;